import React from 'react'
import type { JSONContent } from '../types'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import FormLabel from '@mui/material/FormLabel'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Checkbox from '@mui/material/Checkbox'
import Switch from '@mui/material/Switch'
import MenuItem from '@mui/material/MenuItem'
import FormHelperText from '@mui/material/FormHelperText'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import { parseStyleString } from '../utils'
import {
  convertStylesToSx,
  extractTextFromNode,
  getChoiceOptions,
  getFieldNameByFieldId,
  hasFieldLabel,
} from '../utils'
import type { CurrentSchema } from '../types'
import { RenderFieldInput } from './RenderFieldInput'
import { RenderButton } from './RenderButton'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import type { EditorLayout } from '../types'
import { LocalizedDateProvider } from './LocalizedDateProvider'

interface RenderNodeProps {
  node: JSONContent
  formValues?: Record<string, any>
  onFieldChange?: (name: string, value: any) => void
  currentSchema?: CurrentSchema
  validationErrors?: Record<string, string>
  content?: JSONContent // Полный content для поиска полей по fieldId
  layout?: EditorLayout
  onValidationError?: (fieldName: string, error: string) => void
  /** Callback для автосохранения при потере фокуса полем */
  onBlur?: () => void
}

export function RenderNode({
  node,
  formValues,
  onFieldChange,
  currentSchema,
  validationErrors,
  content,
  layout,
  onValidationError,
  onBlur,
}: RenderNodeProps) {
  switch (node.type) {
    case 'paragraph': {
      const { style, ...paragraphAttrs } = node.attrs || {}
      const paragraphStyle = convertStylesToSx(
        parseStyleString(style as string)
      )
      const paragraphAlign = paragraphAttrs.textAlign || 'left'
      const hasContent = node.content && node.content.length > 0

      return (
        <Typography
          component={'p'}
          sx={{
            textAlign: paragraphAlign as any,
            ...paragraphStyle,
            ...paragraphAttrs,
          }}
        >
          {hasContent && node.content ? (
            node.content.map((child, index) => (
              <RenderNode
                key={index}
                node={child}
                formValues={formValues}
                onFieldChange={onFieldChange}
                currentSchema={currentSchema}
                validationErrors={validationErrors}
                content={content}
                layout={layout}
                onValidationError={onValidationError}
                onBlur={onBlur}
              />
            ))
          ) : (
            <>&nbsp;</>
          )}
        </Typography>
      )
    }
    case 'text': {
      return <>{node.text || '&nbsp;'}</>
    }

    case 'image': {
      const imageAttrs = node.attrs || {}
      const { src, alt = '', width, height, alignment = 'left' } = imageAttrs

      if (!src) {
        return null
      }

      return (
        <Stack
          sx={{
            width: '100%',
            alignItems:
              alignment === 'center'
                ? 'center'
                : alignment === 'right'
                  ? 'flex-end'
                  : 'flex-start',
          }}
        >
          <Box
            sx={{
              width: width ? `${width}px` : 'auto',
              height: height ? `${height}px` : 'auto',
            }}
          >
            <img
              src={src}
              alt={alt}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>
        </Stack>
      )
    }

    case 'text': {
      return <>{node.text || '&nbsp;'}</>
    }

    case 'logo': {
      const imageAttrs = node.attrs || {}
      const { src, size, title = 'logo', alignment = 'left' } = imageAttrs
      const sizes = {
        sm: '40px',
        md: '48px',
        lg: '64px',
      }

      const sizeValue = sizes[size as keyof typeof sizes] || 'auto'

      if (!src) {
        return null
      }

      return (
        <Stack
          sx={{
            width: '100%',
            alignItems:
              alignment === 'center'
                ? 'center'
                : alignment === 'right'
                  ? 'flex-end'
                  : 'flex-start',
          }}
        >
          <Box
            sx={{
              width: sizeValue,
              height: sizeValue,
            }}
          >
            <img
              src={src}
              alt={title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>
        </Stack>
      )
    }

    case 'heading':
      const headingAttrs = node.attrs || {}
      const level = headingAttrs.level || 1
      const variant = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
      const headingStyle = convertStylesToSx(
        parseStyleString(headingAttrs.style as string)
      )
      const headingAlign = headingAttrs.textAlign || 'left'

      return (
        <Typography
          variant={variant}
          gutterBottom
          sx={{
            textAlign: headingAlign as any,
            ...headingStyle,
          }}
        >
          {node.content?.map((child, index) => (
            <RenderNode
              key={index}
              node={child}
              formValues={formValues}
              onFieldChange={onFieldChange}
              currentSchema={currentSchema}
              validationErrors={validationErrors}
              content={content}
              layout={layout}
              onValidationError={onValidationError}
              onBlur={onBlur}
            />
          ))}
        </Typography>
      )

    case 'fieldLabel':
      // Рендерим label как отдельный элемент
      const labelAttrs = node.attrs || {}
      const labelFieldId = labelAttrs.fieldId || ''
      const labelStyle = convertStylesToSx(
        parseStyleString(labelAttrs.style as string)
      )

      // Получаем labelText из layout.fields, если он есть, иначе из node
      const fieldSettings = labelFieldId && layout?.fields?.[labelFieldId]
      const labelText = fieldSettings?.labelText || extractTextFromNode(node)

      // Находим name поля по fieldId (используем для проверки скрытости и ошибок)
      const fieldName =
        content && labelFieldId
          ? getFieldNameByFieldId(content, labelFieldId, layout) || undefined
          : undefined

      // Проверяем, скрыто ли связанное поле и является ли оно required
      let isFieldHidden = false
      let isFieldRequired = false
      if (fieldName) {
        let isHiddenBySchema = false
        let isShownBySchema = false

        // Проверяем, скрыто/показано ли поле в currentSchema (через meta.hidden)
        if (currentSchema) {
          const fieldSchema = currentSchema.properties?.[fieldName]
          isHiddenBySchema = fieldSchema?.meta?.hidden === true
          isShownBySchema = fieldSchema?.meta?.hidden === false
          const isRequiredBySchema =
            currentSchema.required?.includes(fieldName) || false

          // Поле required, если: required === true ИЛИ isRequiredBySchema === true
          isFieldRequired = isRequiredBySchema
        }

        // Проверяем настройки из layout.fields
        const fieldHidden = fieldSettings?.hidden ?? false
        const fieldRequired = fieldSettings?.required ?? false

        // Если условие явно говорит "показать поле" (meta.hidden === false),
        // то оно имеет приоритет над layout.hidden и другими условиями
        if (isShownBySchema) {
          isFieldHidden = false
        } else {
          // Поле скрыто, если: hidden === true ИЛИ isHiddenBySchema === true (и не показано через showField)
          isFieldHidden = fieldHidden || isHiddenBySchema
        }

        // Поле required, если: required === true ИЛИ isRequiredBySchema === true
        isFieldRequired = fieldRequired || isFieldRequired
      }

      // Если поле скрыто, не рендерим label
      if (isFieldHidden) {
        return null
      }

      // Получаем ошибку валидации для этого поля, если она есть
      const errorMessage = fieldName ? validationErrors?.[fieldName] : undefined

      // Проверяем, является ли это поле choice или checkbox (для них ошибка показывается под лейблом)
      const isChoiceOrCheckboxField =
        content && labelFieldId
          ? (() => {
            let isChoiceOrCheckbox = false
            function checkFieldType(node: JSONContent) {
              if (
                (node.type === 'fieldChoiceOption' ||
                  node.type === 'fieldCheckboxOption') &&
                node.attrs?.fieldId === labelFieldId
              ) {
                isChoiceOrCheckbox = true

                return
              }
              if (node.content) {
                node.content.forEach(checkFieldType)
              }
            }
            checkFieldType(content)

            return isChoiceOrCheckbox
          })()
          : false

      // Адаптируем значение alignment под CSS-свойство justify-content
      const alignmentToJustifyContent: Record<string, string> = {
        left: 'flex-start',
        center: 'center',
        right: 'flex-end',
      }
      const justifyContent =
        alignmentToJustifyContent[node.attrs?.alignment as string] ||
        'flex-start'

      return (
        <Box>
          <FormLabel
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent,
              gap: 0.5,
              ...(labelAttrs.color ? { color: labelAttrs.color } : {}),
              ...labelStyle,
            }}
            htmlFor={fieldName || undefined}
          >
            {labelText}
            {isFieldRequired && (
              <ErrorOutlineIcon
                sx={{
                  fontSize: '1rem',
                  color: 'warning.main',
                }}
              />
            )}
          </FormLabel>
          {isChoiceOrCheckboxField && errorMessage && (
            <FormHelperText error sx={{ marginTop: -1, marginBottom: 1 }}>
              {errorMessage}
            </FormHelperText>
          )}
        </Box>
      )

    case 'fieldChoiceOption': {
      if (!content || !formValues || !onFieldChange) {
        return null
      }

      const choiceAttrs = node.attrs || {}
      const fieldId = choiceAttrs.fieldId || ''
      if (!fieldId) {
        return null
      }

      const name =
        getFieldNameByFieldId(content, fieldId, layout) ||
        layout?.fields?.[fieldId]?.name ||
        fieldId ||
        `field-${fieldId || Math.random().toString(36).substr(2, 9)}`

      // Проверяем, скрыто ли поле через currentSchema (условия fieldConditions)
      const fieldSchema = currentSchema?.properties?.[name]
      const isHiddenBySchema = fieldSchema?.meta?.hidden === true
      const isShownBySchema = fieldSchema?.meta?.hidden === false
      const fieldSettings = fieldId && layout?.fields?.[fieldId]
      const isHiddenByLayout = fieldSettings?.hidden ?? false

      // Если условие явно говорит "показать поле" (meta.hidden === false),
      // то оно имеет приоритет над layout.hidden
      const isFieldHidden = isShownBySchema
        ? false
        : isHiddenByLayout || isHiddenBySchema

      if (isFieldHidden) {
        return null
      }

      const optionId =
        choiceAttrs.optionId ||
        `${name}-option-${choiceAttrs.optionIndex || Math.random()}`
      const optionValue = choiceAttrs.value || optionId
      const extractedLabel =
        extractTextFromNode(node) || choiceAttrs.labelText || ''

      const value = name ? (formValues?.[name] ?? '') : ''
      const errorMessage = name ? validationErrors?.[name] : undefined

      const options = getChoiceOptions(content, fieldId)
      // Фильтруем только видимые опции
      const visibleOptions = options.filter((opt) => !opt.hidden)
      const optionIndex = visibleOptions.findIndex(
        (opt) =>
          opt.optionId === choiceAttrs.optionId || opt.value === optionValue
      )
      const labelText =
        extractedLabel ||
        (optionIndex >= 0 ? `Option ${optionIndex + 1}` : 'Choice')

      // Определяем, является ли текущая опция последней
      const isLastOption =
        optionIndex === visibleOptions.length - 1 && optionIndex >= 0

      // Проверяем, есть ли лейбл для этого поля
      const hasLabel = hasFieldLabel(content, fieldId)

      // Показываем ошибку только если лейбла нет и это последняя опция
      const shouldShowError = errorMessage && !hasLabel && isLastOption

      const handleChange = () => {
        onFieldChange?.(name, optionValue)
      }

      return (
        <Box>
          <FormControl
            component="fieldset"
            error={Boolean(shouldShowError)}
            sx={{ width: '100%' }}
          >
            <FormControlLabel
              value={optionValue}
              control={
                <Radio
                  size="small"
                  checked={value === optionValue}
                  onChange={handleChange}
                  name={name}
                  value={optionValue}
                />
              }
              label={labelText}
              sx={{
                margin: 0,
              }}
            />
            {shouldShowError && <FormHelperText>{errorMessage}</FormHelperText>}
          </FormControl>
        </Box>
      )
    }

    case 'fieldCheckboxOption': {
      if (!content || !formValues || !onFieldChange) {
        return null
      }

      const checkboxAttrs = node.attrs || {}
      const fieldId = checkboxAttrs.fieldId || ''
      if (!fieldId) {
        return null
      }

      const name =
        getFieldNameByFieldId(content, fieldId, layout) ||
        layout?.fields?.[fieldId]?.name ||
        fieldId ||
        `field-${fieldId || Math.random().toString(36).substr(2, 9)}`

      // Проверяем, скрыто ли поле через currentSchema (условия fieldConditions)
      const fieldSchema = currentSchema?.properties?.[name]
      const isHiddenBySchema = fieldSchema?.meta?.hidden === true
      const isShownBySchema = fieldSchema?.meta?.hidden === false
      const fieldSettings = fieldId && layout?.fields?.[fieldId]
      const isHiddenByLayout = fieldSettings?.hidden ?? false

      // Если условие явно говорит "показать поле" (meta.hidden === false),
      // то оно имеет приоритет над layout.hidden
      const isFieldHidden = isShownBySchema
        ? false
        : isHiddenByLayout || isHiddenBySchema

      if (isFieldHidden) {
        return null
      }

      const optionId =
        checkboxAttrs.optionId ||
        `${name}-option-${checkboxAttrs.optionIndex || Math.random()}`
      const optionValue = checkboxAttrs.value || optionId
      const extractedLabel =
        extractTextFromNode(node) || checkboxAttrs.labelText || ''

      const currentValue = name ? (formValues?.[name] ?? []) : []
      const valueArray: string[] = Array.isArray(currentValue)
        ? currentValue
        : []
      const errorMessage = name ? validationErrors?.[name] : undefined

      const options = getChoiceOptions(content, fieldId)
      const optionIndex = options.findIndex(
        (opt) =>
          opt.optionId === checkboxAttrs.optionId || opt.value === optionValue
      )
      const labelText =
        extractedLabel ||
        (optionIndex >= 0 ? `Option ${optionIndex + 1}` : 'Option')

      // Определяем, является ли текущая опция последней
      const isLastOption =
        optionIndex === options.length - 1 && optionIndex >= 0

      // Проверяем, есть ли лейбл для этого поля
      const hasLabel = hasFieldLabel(content, fieldId)

      // Показываем ошибку только если лейбла нет и это последняя опция
      const shouldShowError = errorMessage && !hasLabel && isLastOption

      const handleChange = () => {
        const exists = valueArray.includes(optionValue)
        const newValue = exists
          ? valueArray.filter((v) => v !== optionValue)
          : [...valueArray, optionValue]

        onFieldChange?.(name, newValue)
      }

      return (
        <Box>
          <FormControl
            component="fieldset"
            error={Boolean(shouldShowError)}
            sx={{ width: '100%' }}
          >
            <FormControlLabel
              value={optionValue}
              control={
                <Checkbox
                  size="small"
                  checked={valueArray.includes(optionValue)}
                  onChange={handleChange}
                  name={name}
                  value={optionValue}
                />
              }
              label={labelText}
              sx={{
                margin: 0,
              }}
            />
            {shouldShowError && <FormHelperText>{errorMessage}</FormHelperText>}
          </FormControl>
        </Box>
      )
    }

    case 'booleanFieldInput': {
      if (!content || !formValues || !onFieldChange) {
        return null
      }

      const attrs = node.attrs || {}
      const fieldId = attrs.fieldId || ''
      if (!fieldId) {
        return null
      }

      const name =
        getFieldNameByFieldId(content, fieldId, layout) ||
        layout?.fields?.[fieldId]?.name ||
        fieldId ||
        `field-${fieldId || Math.random().toString(36).substr(2, 9)}`

      const value = !!(name ? formValues?.[name] : false)
      const errorMessage = name ? validationErrors?.[name] : undefined

      const handleChange = (event: any) => {
        onFieldChange?.(name, Boolean(event.target.checked))
      }

      const labelText =
        layout?.fields?.[fieldId]?.labelText ||
        layout?.fields?.[fieldId]?.name ||
        'Checkbox'

      // Если для этого поля уже есть отдельный fieldLabel (например, во второй колонке),
      // не дублируем текст лейбла рядом с переключателем.
      const hasLabelNode = content ? hasFieldLabel(content, fieldId) : false

      const displayType =
        layout?.fields?.[fieldId]?.displayType === 'switch'
          ? 'switch'
          : 'checkbox'

      // Проверяем, скрыто ли поле через currentSchema (условия fieldConditions)
      const fieldSchema = currentSchema?.properties?.[name]
      const isHiddenBySchema = fieldSchema?.meta?.hidden === true
      const isShownBySchema = fieldSchema?.meta?.hidden === false
      const fieldSettings = layout?.fields?.[fieldId]
      const isHiddenByLayout = fieldSettings?.hidden ?? false

      // Если условие явно говорит "показать поле" (meta.hidden === false),
      // то оно имеет приоритет над layout.hidden
      const isFieldHidden = isShownBySchema
        ? false
        : isHiddenByLayout || isHiddenBySchema

      if (isFieldHidden) {
        return null
      }

      return (
        <Box data-field-name={name}>
          <FormControl
            component="fieldset"
            error={Boolean(errorMessage)}
            sx={{ width: '100%' }}
          >
            <FormControlLabel
              control={
                displayType === 'switch' ? (
                  <Switch
                    size="small"
                    checked={value}
                    onChange={handleChange}
                    name={name}
                    id={name}
                  />
                ) : (
                  <Checkbox
                    size="small"
                    sx={{ p: 0 }}
                    checked={value}
                    onChange={handleChange}
                    name={name}
                    id={name}
                  />
                )
              }
              label={hasLabelNode ? '' : labelText}
              sx={{
                margin: 0,
              }}
            />
            {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
          </FormControl>
        </Box>
      )
    }

    case 'fieldSelectOption': {
      if (!content || !formValues || !onFieldChange) {
        return null
      }

      const selectAttrs = node.attrs || {}
      const fieldId = selectAttrs.fieldId || ''
      if (!fieldId) {
        return null
      }

      const name =
        getFieldNameByFieldId(content, fieldId, layout) ||
        layout?.fields?.[fieldId]?.name ||
        fieldId ||
        `field-${fieldId || Math.random().toString(36).substr(2, 9)}`

      // Проверяем, скрыто ли поле через currentSchema (условия fieldConditions)
      const fieldSchema = currentSchema?.properties?.[name]
      const isHiddenBySchema = fieldSchema?.meta?.hidden === true
      const isShownBySchema = fieldSchema?.meta?.hidden === false
      const fieldSettings = fieldId && layout?.fields?.[fieldId]
      const isHiddenByLayout = fieldSettings?.hidden ?? false

      // Если условие явно говорит "показать поле" (meta.hidden === false),
      // то оно имеет приоритет над layout.hidden
      const isFieldHidden = isShownBySchema
        ? false
        : isHiddenByLayout || isHiddenBySchema

      if (isFieldHidden) {
        return null
      }

      const value = name ? (formValues?.[name] ?? '') : ''
      const errorMessage = name ? validationErrors?.[name] : undefined

      const options = getChoiceOptions(content, fieldId)
      const isFirstOption =
        options[0]?.optionId === selectAttrs.optionId ||
        (!options[0]?.optionId && options.length > 0)

      // Рендерим Select только для первой опции
      if (!isFirstOption) {
        return null
      }

      const handleChange = (event: any) => {
        onFieldChange?.(name, event.target.value)
      }

      return (
        <Box data-field-name={name}>
          <TextField
            select
            fullWidth
            value={value}
            onChange={handleChange}
            name={name}
            error={Boolean(errorMessage)}
            helperText={errorMessage}
            margin="dense"
            SelectProps={{
              displayEmpty: true,
            }}
          >
            <MenuItem value="">
              <em>Select an option</em>
            </MenuItem>
            {options.map((option, index) => {
              const displayLabel =
                option.label || option.value || `Option ${index + 1}`

              return (
                <MenuItem
                  key={option.optionId}
                  value={option.value || option.optionId}
                >
                  {displayLabel}
                </MenuItem>
              )
            })}
          </TextField>
        </Box>
      )
    }

    case 'fieldMultiselectOption': {
      if (!content || !formValues || !onFieldChange) {
        return null
      }

      const multiselectAttrs = node.attrs || {}
      const fieldId = multiselectAttrs.fieldId || ''
      if (!fieldId) {
        return null
      }

      const name =
        getFieldNameByFieldId(content, fieldId, layout) ||
        layout?.fields?.[fieldId]?.name ||
        fieldId ||
        `field-${fieldId || Math.random().toString(36).substr(2, 9)}`

      // Проверяем, скрыто ли поле через currentSchema (условия fieldConditions)
      const fieldSchema = currentSchema?.properties?.[name]
      const isHiddenBySchema = fieldSchema?.meta?.hidden === true
      const isShownBySchema = fieldSchema?.meta?.hidden === false
      const fieldSettings = fieldId && layout?.fields?.[fieldId]
      const isHiddenByLayout = fieldSettings?.hidden ?? false

      // Если условие явно говорит "показать поле" (meta.hidden === false),
      // то оно имеет приоритет над layout.hidden
      const isFieldHidden = isShownBySchema
        ? false
        : isHiddenByLayout || isHiddenBySchema

      if (isFieldHidden) {
        return null
      }

      const currentValue = name ? (formValues?.[name] ?? []) : []
      const valueArray: string[] = Array.isArray(currentValue)
        ? currentValue
        : []
      const errorMessage = name ? validationErrors?.[name] : undefined

      const options = getChoiceOptions(content, fieldId)
      const isFirstOption =
        options[0]?.optionId === multiselectAttrs.optionId ||
        (!options[0]?.optionId && options.length > 0)

      // Рендерим Select только для первой опции
      if (!isFirstOption) {
        return null
      }

      const handleChange = (event: any) => {
        const selectedValues = event.target.value as string[]
        onFieldChange?.(name, selectedValues)
      }

      return (
        <Box data-field-name={name}>
          <TextField
            select
            fullWidth
            size="small"
            value={valueArray}
            onChange={handleChange}
            name={name}
            error={Boolean(errorMessage)}
            helperText={errorMessage}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (selected) => {
                const values = selected as string[]
                if (!values || values.length === 0) {
                  return <em>Select options</em>
                }

                return values.join(', ')
              },
            }}
          >
            {options.map((option, index) => {
              const displayLabel =
                option.label || option.value || `Option ${index + 1}`

              return (
                <MenuItem
                  key={option.optionId}
                  value={option.value || option.optionId}
                >
                  {displayLabel}
                </MenuItem>
              )
            })}
          </TextField>
        </Box>
      )
    }

    case 'shortAnswerFieldInput':
      return (
        <RenderFieldInput
          node={node}
          type="text"
          formValues={formValues}
          onFieldChange={onFieldChange}
          currentSchema={currentSchema}
          validationErrors={validationErrors}
          content={content}
          layout={layout}
          onValidationError={onValidationError}
          onBlur={onBlur}
        />
      )

    case 'longAnswerFieldInput':
      return (
        <RenderFieldInput
          node={node}
          type="textarea"
          formValues={formValues}
          onFieldChange={onFieldChange}
          currentSchema={currentSchema}
          validationErrors={validationErrors}
          content={content}
          layout={layout}
          onValidationError={onValidationError}
          onBlur={onBlur}
        />
      )

    case 'numberFieldInput':
      return (
        <RenderFieldInput
          node={node}
          type="number"
          formValues={formValues}
          onFieldChange={onFieldChange}
          currentSchema={currentSchema}
          validationErrors={validationErrors}
          content={content}
          layout={layout}
          onValidationError={onValidationError}
          onBlur={onBlur}
        />
      )

    case 'emailFieldInput':
      return (
        <RenderFieldInput
          node={node}
          type="email"
          formValues={formValues}
          onFieldChange={onFieldChange}
          currentSchema={currentSchema}
          validationErrors={validationErrors}
          content={content}
          layout={layout}
          onValidationError={onValidationError}
          onBlur={onBlur}
        />
      )

    case 'phoneNumberFieldInput':
      return (
        <RenderFieldInput
          node={node}
          type="tel"
          formValues={formValues}
          onFieldChange={onFieldChange}
          currentSchema={currentSchema}
          validationErrors={validationErrors}
          content={content}
          layout={layout}
          onValidationError={onValidationError}
          onBlur={onBlur}
        />
      )

    case 'linkFieldInput':
      return (
        <RenderFieldInput
          node={node}
          type="url"
          formValues={formValues}
          onFieldChange={onFieldChange}
          currentSchema={currentSchema}
          validationErrors={validationErrors}
          content={content}
          layout={layout}
          onValidationError={onValidationError}
          onBlur={onBlur}
        />
      )

    case 'dateFieldInput':
      return (
        <LocalizedDateProvider>
          <RenderFieldInput
            node={node}
            type="date"
            formValues={formValues}
            onFieldChange={onFieldChange}
            currentSchema={currentSchema}
            validationErrors={validationErrors}
            content={content}
            layout={layout}
            onValidationError={onValidationError}
            onBlur={onBlur}
          />
        </LocalizedDateProvider>
      )

    case 'timeFieldInput':
      return (
        <LocalizedDateProvider>
          <RenderFieldInput
            node={node}
            type="time"
            formValues={formValues}
            onFieldChange={onFieldChange}
            currentSchema={currentSchema}
            validationErrors={validationErrors}
            content={content}
            layout={layout}
            onValidationError={onValidationError}
            onBlur={onBlur}
          />
        </LocalizedDateProvider>
      )

    case 'dateTimeFieldInput':
      return (
        <LocalizedDateProvider>
          <RenderFieldInput
            node={node}
            type="datetime-local"
            formValues={formValues}
            onFieldChange={onFieldChange}
            currentSchema={currentSchema}
            validationErrors={validationErrors}
            content={content}
            layout={layout}
            onValidationError={onValidationError}
            onBlur={onBlur}
          />
        </LocalizedDateProvider>
      )

    case 'fileUploaderFieldInput':
      return (
        <RenderFieldInput
          node={node}
          type="file"
          formValues={formValues}
          onFieldChange={onFieldChange}
          currentSchema={currentSchema}
          validationErrors={validationErrors}
          content={content}
          layout={layout}
          onValidationError={onValidationError}
          onBlur={onBlur}
        />
      )

    case 'button':
      return <RenderButton node={node} />

    case 'variable':
      // Variables не рендерятся в preview
      return null

    case 'fieldConditions':
      // Условия не рендерятся в preview
      return null

    case 'booleanFieldRow': {
      // Горизонтальное расположение booleanFieldInput + fieldLabel
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          {node.content?.map((child, index) => (
            <RenderNode
              key={index}
              node={child}
              formValues={formValues}
              onFieldChange={onFieldChange}
              currentSchema={currentSchema}
              validationErrors={validationErrors}
              onValidationError={onValidationError}
              content={content}
              layout={layout}
              onBlur={onBlur}
            />
          ))}
        </Box>
      )
    }

    case 'section':
      const sectionAttrs = node.attrs || {}
      const {
        borderRadius = 0,
        backgroundColor = '#ffffff',
        align = 'left',
        borderWidth = 1,
        borderColor = '#000000',
        marginTop = 0,
        marginRight = 0,
        marginBottom = 0,
        marginLeft = 0,
        paddingTop = 5,
        paddingRight = 5,
        paddingBottom = 5,
        paddingLeft = 5,
      } = sectionAttrs
      const sectionStyle = convertStylesToSx(
        parseStyleString(sectionAttrs.style as string)
      )

      return (
        <Box
          sx={{
            borderRadius: `${borderRadius}px`,
            backgroundColor,
            textAlign: align as any,
            borderWidth: `${borderWidth}px`,
            borderColor,
            borderStyle: 'solid',
            marginTop: `${marginTop}px`,
            marginRight: `${marginRight}px`,
            marginBottom: `${marginBottom}px`,
            marginLeft: `${marginLeft}px`,
            paddingTop: `${paddingTop}px`,
            paddingRight: `${paddingRight}px`,
            paddingBottom: `${paddingBottom}px`,
            paddingLeft: `${paddingLeft}px`,
            ...sectionStyle,
          }}
        >
          {node.content?.map((child, index) => (
            <RenderNode
              key={index}
              node={child}
              formValues={formValues}
              onFieldChange={onFieldChange}
              currentSchema={currentSchema}
              validationErrors={validationErrors}
              content={content}
              layout={layout}
              onValidationError={onValidationError}
              onBlur={onBlur}
            />
          ))}
        </Box>
      )

    case 'columns':
      const columnsAttrs = node.attrs || {}
      const columnsStyle = convertStylesToSx(
        parseStyleString(columnsAttrs.style as string)
      )

      const gap = columnsAttrs.gap || 8

      return (
        <Grid
          container
          spacing={gap / 8}
          sx={{
            ...columnsStyle,
          }}
        >
          {node.content?.map((child, index) => (
            <RenderNode
              key={index}
              node={child}
              formValues={formValues}
              onFieldChange={onFieldChange}
              currentSchema={currentSchema}
              validationErrors={validationErrors}
              content={content}
              layout={layout}
              onValidationError={onValidationError}
              onBlur={onBlur}
            />
          ))}
        </Grid>
      )

    case 'column': {
      const columnAttrs = node.attrs || {}
      const width = columnAttrs.width || 'auto'
      const widthUnit = columnAttrs.widthUnit || 'percent'
      const verticalAlign = columnAttrs.verticalAlign || 'top'
      const align = columnAttrs.align || 'left'
      const columnPaddingTop = columnAttrs.paddingTop || 0
      const columnPaddingRight = columnAttrs.paddingRight || 0
      const columnPaddingBottom = columnAttrs.paddingBottom || 0
      const columnPaddingLeft = columnAttrs.paddingLeft || 0
      const columnStyle = convertStylesToSx(
        parseStyleString(columnAttrs.style as string)
      )

      // Пробуем сконвертировать width (в %) в сетку MUI (12 колонок),
      // чтобы избежать flex-basis: 100% и уважать настройки column.width.
      let gridXs: number | undefined = undefined
      if (width !== 'auto' && widthUnit === 'percent') {
        const numericWidth =
          typeof width === 'number' ? width : parseFloat(String(width))
        if (!Number.isNaN(numericWidth) && numericWidth > 0) {
          gridXs = Math.min(
            12,
            Math.max(1, Math.round((numericWidth / 100) * 12))
          )
        }
      }

      const computedWidth =
        width === 'auto'
          ? undefined
          : widthUnit === 'px'
            ? `${width}px`
            : `${width}%`

      return (
        //@ts-ignore
        <Grid
          item
          xs={width === 'auto' || widthUnit === 'px' ? 12 : gridXs}
          sm={width === 'auto' || widthUnit === 'px' ? 'auto' : gridXs}
          sx={{
            width: computedWidth,
            verticalAlign,
            textAlign: align,
            paddingTop: `${columnPaddingTop}px`,
            paddingRight: `${columnPaddingRight}px`,
            paddingBottom: `${columnPaddingBottom}px`,
            paddingLeft: `${columnPaddingLeft}px`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            // flex: 1,
            ...columnStyle,
          }}
        >
          {node.content?.map((child, index) => (
            <RenderNode
              key={index}
              node={child}
              formValues={formValues}
              onFieldChange={onFieldChange}
              currentSchema={currentSchema}
              validationErrors={validationErrors}
              content={content}
              layout={layout}
              onValidationError={onValidationError}
              onBlur={onBlur}
            />
          ))}
        </Grid>
      )
    }

    case 'bulletList':
    case 'orderedList': {
      const listAttrs = node.attrs || {}
      const listStyle = convertStylesToSx(
        parseStyleString(listAttrs.style as string)
      )
      const indent = listAttrs.indent
      const after = listAttrs.after


      const isOrdered = node.type === 'orderedList'
      const start = listAttrs.start

      return (
        <Box
          component={isOrdered ? 'ol' : 'ul'}
          {...(isOrdered && start != null && start !== 1 ? { start } : {})}
          sx={{
            marginTop: 0,
            marginBottom: 0,
            paddingLeft: '1.5em',
            listStyleType: isOrdered ? 'decimal' : 'disc',
            listStylePosition: 'outside',
            '& > li': {
              paddingLeft: indent,
              '&:not(:last-child)': {
                marginBottom: after,
              },
              '& p': {
                margin: 0,
              },
            },
            '& > li:last-child': {
              marginBottom: 0,
            },
            ...listStyle,
          }}
        >
          {node.content?.map((child, index) => (
            <RenderNode
              key={index}
              node={child}
              formValues={formValues}
              onFieldChange={onFieldChange}
              currentSchema={currentSchema}
              validationErrors={validationErrors}
              content={content}
              layout={layout}
              onValidationError={onValidationError}
              onBlur={onBlur}
            />
          ))}
        </Box>
      )
    }

    case 'listItem': {
      const itemAttrs = node.attrs || {}
      const itemStyle = convertStylesToSx(
        parseStyleString(itemAttrs.style as string)
      )

      return (
        <Box component="li" sx={{ display: 'list-item', ...itemStyle }}>
          {node.content?.map((child, index) => (
            <RenderNode
              key={index}
              node={child}
              formValues={formValues}
              onFieldChange={onFieldChange}
              currentSchema={currentSchema}
              validationErrors={validationErrors}
              content={content}
              layout={layout}
              onValidationError={onValidationError}
              onBlur={onBlur}
            />
          ))}
        </Box>
      )
    }

    case 'page':
      // В step форме page рендерится без дополнительных стилей (уже обработан в FormRender)
      const pageStyle = convertStylesToSx(
        parseStyleString(node.attrs?.style as string)
      )

      return (
        <Box sx={pageStyle}>
          {node.content?.map((child, index) => (
            <RenderNode
              key={index}
              node={child}
              formValues={formValues}
              onFieldChange={onFieldChange}
              currentSchema={currentSchema}
              validationErrors={validationErrors}
              content={content}
              layout={layout}
              onValidationError={onValidationError}
              onBlur={onBlur}
            />
          ))}
        </Box>
      )

    default:
      // Для неизвестных типов просто рендерим содержимое
      if (node.content) {
        return (
          <>
            {node.content.map((child, index) => (
              <RenderNode
                key={index}
                node={child}
                formValues={formValues}
                onFieldChange={onFieldChange}
                currentSchema={currentSchema}
                validationErrors={validationErrors}
                content={content}
                layout={layout}
                onValidationError={onValidationError}
                onBlur={onBlur}
              />
            ))}
          </>
        )
      }

      return null
  }
}
